<template>
  <UIInputAutocomplete
    ref="inputRef"
    v-model="modelValue"
    :data
    fetchable
    placeholder="Find an option"
    :disabled
    @fetch:data="handleSearch"
    @click:item="handleSelect"
    @ready="handleReady"
  >
    <template #trailing>
      <component :is="loading ? UIPreloader : MagnifyingGlassIcon" />
    </template>
    <template #item="{ item, itemClasses, onClick }">
      <div class="ui-dropdown-list-item" :class="itemClasses" @click="onClick">
        <div class="asset-dropdown-item">
          <span class="asset-dropdown-item__label">{{ item.label }}</span>
          <span v-if="item.label !== item.value" class="asset-dropdown-item__ticker">{{ item.value }}</span>
        </div>
      </div>
    </template>
  </UIInputAutocomplete>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue'
import { useDebounceFn } from '@vueuse/core'

import { RepositoryCurrency } from '@types'
import { AssetClass } from '..'

import { THROTTLE_DELAY } from '@/const/common'

import { useRepositoriesStore } from '@/store/repositories/index'

import { UIInputAutocomplete } from '@ui/fields'
import { handleCatchedError } from '@/helpers/common'
import { AxiosError } from 'axios'
import { UIPreloader } from '@ui/core'
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline'

const MIN_SEARCH_LENGTH = 2

type Props = {
  instance: AssetClass
  disabled?: boolean
}

type Emits = {
  select: [label: string, ticker: string]
  ready: [el: HTMLInputElement]
}

defineProps<Props>()
const emit = defineEmits<Emits>()

const repositoriesStore = useRepositoriesStore()

const modelValue = defineModel<string>()

const inputRef = useTemplateRef('inputRef')

let abortController: AbortController

const searchResults = ref<RepositoryCurrency[]>()
const loading = ref(false)

const currenciesList = computed(() => repositoriesStore.getCurrencies)

const data = computed(() => {
  let results
  if (!!searchResults.value) {
    results = searchResults.value
  } else {
    results = currenciesList.value
  }
  return results.map(item => ({
    value: item.ticker,
    label: item.name
  }))
})

const handleSearch = async (data: string) => {
  loading.value = true
  await debouncedFetch(data)
  loading.value = false
}

const fetch = async (data: string) => {
  try {
    abortController?.abort()
    if (!data) {
      searchResults.value = undefined
      return
    }
    if (data.length < MIN_SEARCH_LENGTH) return
    abortController = new AbortController()
    const result = await repositoriesStore.fetchCurrencies(data, false, abortController)
    searchResults.value = result
  } catch (error) {
    const e = error as AxiosError
    handleCatchedError(e.message)
  }
}

const debouncedFetch = useDebounceFn(fetch, THROTTLE_DELAY)

const handleSelect = (value: string) => {
  const result = data.value.find(item => item.value === value)
  const ticker = result?.value ?? ''
  const label = result?.label ?? ticker
  emit('select', label, ticker)
}

const handleReady = (el: HTMLInputElement) => {
  emit('ready', el)
}

onMounted(() => {
  if (modelValue.value && !currenciesList.value.find(item => item.ticker === modelValue.value)) {
    inputRef.value?.setSearchValue(modelValue.value, false)
  }
})
</script>
